import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
} from "@mui/material";
import getDeviceInfo from "../../utils/deviceInfo";

const FIELD_METADATA = {
  name: { title: "First Name", mode: "required", fixed: true },
  lastName: { title: "Last Name", mode: "required", fixed: true },
  email: { title: "Email", mode: "required", fixed: true },
  middleName: { title: "Middle Name", mode: "optional" },
  prefix: { title: "Prefix", mode: "optional" },
  suffix: { title: "Suffix", mode: "optional" },
  mobileNumber: { title: "Mobile Number", mode: "required" },
  birthdate: { title: "Birthdate", mode: "optional" },
  gender: { title: "Gender", mode: "optional" },
  street: { title: "Street", mode: "required" },
  city: { title: "City", mode: "required" },
  province: { title: "Province", mode: "required" },
  country: { title: "Country", mode: "required" },
  zip: { title: "Zip Code", mode: "required" },
  termsAccepted: { title: "Terms and Conditions", mode: "required" },
};

const RegistrationForm = ({ setActiveStep, formData, setFormData, registrationFields }) => {
  const fieldRefs = useRef({});
  const [errors, setErrors] = useState({});

  // Safely parse registrationFields string
  const parsedRegistrationFields = (() => {
    try {
      return typeof registrationFields === "string" ? JSON.parse(registrationFields) : registrationFields || {};
    } catch (err) {
      console.warn("Failed to parse registrationFields:", err);
      return {};
    }
  })();

  const activeFields = Object.entries(FIELD_METADATA).reduce((acc, [key, meta]) => {
    const override = parsedRegistrationFields[key];
    if (override === "disabled") return acc;
    acc[key] = {
      ...meta,
      mode: override || meta.mode,
    };
    return acc;
  }, {});

  useEffect(() => {
    const deviceInfo = getDeviceInfo();
    setFormData((prev) => ({
      ...prev,
      os: deviceInfo.os,
      browser: deviceInfo.browser,
      deviceType: deviceInfo.deviceType,
    }));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            lat: position.coords.latitude.toString(),
            long: position.coords.longitude.toString(),
          }));
        },
        (error) => console.warn("Geolocation error:", error.message)
      );
    } else {
      console.warn("Geolocation is not supported");
    }
  }, [setFormData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredFields = Object.entries(activeFields)
      .filter(([, config]) => config.mode === "required")
      .map(([field]) => field);

    let newErrors = {};
    let firstErrorField = null;

    requiredFields.forEach((key) => {
      if (!formData[key] || (typeof formData[key] === "boolean" && !formData[key])) {
        newErrors[key] = true;
        if (!firstErrorField) firstErrorField = key;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      if (firstErrorField && fieldRefs.current[firstErrorField]) {
        const element = fieldRefs.current[firstErrorField];
        if (element.scrollIntoView) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      return;
    }

    console.log("Form Data Submitted:", formData);
    setActiveStep((prevStep) => prevStep + 1);
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <img
          src="https://reg.govirtual.ph/assets/img/slider/banner.jpg"
          alt="Logo"
          style={{
            maxWidth: "100%",
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>

      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Register to the Event
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {Object.entries(activeFields).map(([name, { title, mode }]) => {
            const isRequired = mode === "required";
            const type = name === "birthdate" ? "date" : name === "email" ? "email" : "text";

            if (name === "gender") {
              return (
                <Grid item xs={12} key={name}>
                  <FormControl fullWidth error={!!errors.gender}>
                    <Select
                      displayEmpty
                      name="gender"
                      value={formData.gender || ""}
                      onChange={handleChange}
                      inputRef={(el) => (fieldRefs.current["gender"] = el?.parentNode || el)}
                    >
                      <MenuItem value="" disabled>
                        Select Gender
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {errors.gender && <FormHelperText>This field is required</FormHelperText>}
                  </FormControl>
                </Grid>
              );
            }

            if (name === "termsAccepted") {
              return (
                <Grid item xs={12} key={name}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="termsAccepted"
                        checked={formData.termsAccepted || false}
                        onChange={handleChange}
                        sx={{ color: errors.termsAccepted ? "red" : undefined }}
                      />
                    }
                    label="Agree to terms and conditions"
                  />
                  {errors.termsAccepted && (
                    <Typography variant="body2" color="error">
                      You must accept the terms
                    </Typography>
                  )}
                </Grid>
              );
            }

            return (
              <Grid item xs={12} key={name}>
                <TextField
                  fullWidth
                  label={title}
                  name={name}
                  type={type}
                  value={formData[name] || ""}
                  onChange={handleChange}
                  error={!!errors[name]}
                  helperText={errors[name] ? "This field is required" : ""}
                  inputRef={(el) => (fieldRefs.current[name] = el?.parentNode || el)}
                  required={isRequired}
                />
              </Grid>
            );
          })}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ borderRadius: "50px", bgcolor: "#1976d2", color: "white" }}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default RegistrationForm;
